import type { QuoteRow } from '@repo/api-contracts';
import { ArrowRight } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { QuoteStatusBadge, quoteImageAlt } from '@/entities/quote';
import { myQuoteDetailPath } from '@/shared/config';
import { formatCount, formatDate, formatKrw } from '@/shared/lib';
import { DesignThumbnail } from '@/shared/ui';

interface MyQuoteRowProps {
  readonly row: QuoteRow;
}

const CELL_CLASS = 'py-5 pr-4 align-middle last:pr-0 sm:py-6';

/**
 * 내 견적 한 줄 — 견적 번호 링크가 줄 전체를 덮어(after:inset-0) 어디를 눌러도 상세로 간다.
 * 상품명 칸 왼쪽에 견적 썸네일(없거나 못 불러오면 회색 칸 — 문구는 화면낭독기에만)을 둔다. 표 머리는 그대로다.
 * 썸네일 칸(`relative`·`@container`)은 링크 덮개(after:inset-0) 위에 쌓이므로 pointer-events-none 으로 눌림을 덮개에 넘긴다.
 * sm 미만(375px)은 그림을 위, 글자를 아래로 쌓아 상품명 칸이 좁아지지 않게 하고, 수량·날짜 줄은 숫자 중간에서 끊지 않는다.
 * 좁은 화면은 수량·요청일·상태 칸을 숨기고 그 값을 번호·상품 칸 아래에 접어 보여 준다.
 */
export const MyQuoteRow = ({ row }: MyQuoteRowProps): ReactElement => (
  <tr className="group relative border-b border-foreground/10 transition-colors hover:bg-muted/50">
    <td className={`${CELL_CLASS} pl-1 whitespace-nowrap sm:pl-2`}>
      <Link
        to={myQuoteDetailPath(row.id)}
        className="font-semibold tracking-[-0.01em] tabular-nums after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring focus-visible:after:ring-inset"
      >
        {row.quoteNumber}
      </Link>
      <QuoteStatusBadge
        status={row.status}
        orderDisplayStatus={row.orderDisplayStatus}
        className="mt-2 flex w-fit md:hidden"
      />
    </td>
    <td className={`${CELL_CLASS} wrap-anywhere`}>
      <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4">
        <DesignThumbnail
          url={row.thumbnailUrl ?? row.productImageUrl}
          alt={quoteImageAlt(row, row.productName, `${row.quoteNumber} 디자인 미리보기`)}
          className="pointer-events-none size-11 shrink-0 sm:size-14"
        />
        <div className="min-w-0">
          <p className="font-medium">{row.productName}</p>
          <p className="mt-1 text-xs whitespace-nowrap text-muted-foreground tabular-nums md:hidden">
            {formatCount(row.quantity)}개 · {formatDate(row.createdAt)}
          </p>
        </div>
      </div>
    </td>
    <td className={`${CELL_CLASS} hidden text-right tabular-nums md:table-cell`}>
      {formatCount(row.quantity)}개
    </td>
    <td className={`${CELL_CLASS} hidden text-muted-foreground tabular-nums md:table-cell`}>
      {formatDate(row.createdAt)}
    </td>
    <td className={`${CELL_CLASS} hidden md:table-cell`}>
      <QuoteStatusBadge status={row.status} orderDisplayStatus={row.orderDisplayStatus} />
    </td>
    <td className={`${CELL_CLASS} text-right whitespace-nowrap tabular-nums`}>
      {row.latestIssuedTotalAmount === null ? (
        <span className="text-xs text-muted-foreground">발행 전</span>
      ) : (
        <span className="font-bold tracking-[-0.01em]">
          {formatKrw(row.latestIssuedTotalAmount)}
        </span>
      )}
    </td>
    <td className={`${CELL_CLASS} hidden pr-2 text-right lg:table-cell`}>
      <span
        aria-hidden
        className="inline-flex items-center gap-1 text-sm whitespace-nowrap text-muted-foreground transition-colors group-hover:text-foreground"
      >
        상세 보기
        <ArrowRight className="size-3.5" strokeWidth={1.5} />
      </span>
    </td>
  </tr>
);
